import { useCallback, useMemo, useState } from 'react';

import { PaletteRegistryContext, type PaletteRegistry } from './palette-registry';

import type { PaletteAction } from './CommandPalette';
import type { ReactElement, ReactNode } from 'react';

/**
 * Holds the commands the mounted pages have published to the app's one ⌘K
 * palette. Wraps the AppShell, so anything inside `<Outlet/>` can publish and the
 * shell's palette can read.
 *
 * See `palette-registry.ts` for the contract and for why the seam exists at all.
 */
export function PaletteActionsProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const [bySource, setBySource] = useState<Readonly<Record<string, readonly PaletteAction[]>>>({});

  const publish = useCallback((sourceId: string, actions: readonly PaletteAction[]) => {
    setBySource((current) => ({ ...current, [sourceId]: actions }));
  }, []);

  const retract = useCallback((sourceId: string) => {
    setBySource((current) => {
      if (!(sourceId in current)) return current;
      // Rebuilt by filtering rather than `delete`: a dynamic delete on a
      // string-keyed record is both a lint finding and a deopt, and this runs on
      // every page unmount.
      return Object.fromEntries(Object.entries(current).filter(([id]) => id !== sourceId));
    });
  }, []);

  // Sorted by source id, so the palette's order can never depend on which page
  // happened to mount first.
  const actions = useMemo(
    () =>
      Object.keys(bySource)
        .sort()
        .flatMap((id) => bySource[id] ?? []),
    [bySource],
  );

  const value = useMemo<PaletteRegistry>(
    () => ({ actions, publish, retract }),
    [actions, publish, retract],
  );

  return (
    <PaletteRegistryContext.Provider value={value}>{children}</PaletteRegistryContext.Provider>
  );
}
